import { DEFAULT_FILTERS } from '../types/analytics';
import type {
  AcquisitionMetrics, ActivationMetrics, AnalyticsDataSource, CostMetrics, DataEnvelope, DateRange,
  FeatureUsageMetrics, OverviewMetrics, PriceComparisonMetrics, ReceiptMetrics,
  ReliabilityMetrics, RetentionMetrics, QueryFilters,
} from '../types/analytics';

type Endpoint = 'overview' | 'acquisition' | 'activation' | 'retention' | 'features' | 'receipts' | 'price-comparison' | 'costs' | 'reliability';

export class AnalyticsApiError extends Error {
  constructor(public readonly status: number, message: string) { super(message); }
}

export class ApiAnalyticsDataSource implements AnalyticsDataSource {
  readonly kind = 'api' as const;

  constructor(
    private readonly baseUrl = '/api/analytics',
    private readonly getAccessToken: () => Promise<string | null> = async () => null,
  ) {}

  private async get<T>(endpoint: Endpoint, range: DateRange, signal?: AbortSignal, filters: QueryFilters = DEFAULT_FILTERS): Promise<DataEnvelope<T>> {
    const params = new URLSearchParams({ start: range.start, end: range.end, timezone: range.timezone, scope: filters.scope, segment: filters.segment });
    const token = await this.getAccessToken();
    const response = await fetch(`${this.baseUrl}/${endpoint}?${params}`, {
      signal,
      credentials: 'include',
      headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!response.ok) throw new AnalyticsApiError(response.status, response.status === 403 ? 'Admin access required.' : 'Analytics request failed.');
    return response.json() as Promise<DataEnvelope<T>>;
  }

  getOverview = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<OverviewMetrics>('overview', range, signal, filters);
  getAcquisition = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<AcquisitionMetrics>('acquisition', range, signal, filters);
  getActivationFunnel = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<ActivationMetrics>('activation', range, signal, filters);
  getRetention = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<RetentionMetrics>('retention', range, signal, filters);
  getFeatureUsage = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<FeatureUsageMetrics>('features', range, signal, filters);
  getReceiptMetrics = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<ReceiptMetrics>('receipts', range, signal, filters);
  getPriceComparisonMetrics = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<PriceComparisonMetrics>('price-comparison', range, signal, filters);
  getCostMetrics = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<CostMetrics>('costs', range, signal, filters);
  getSystemHealth = (range: DateRange, signal?: AbortSignal, filters?: QueryFilters) => this.get<ReliabilityMetrics>('reliability', range, signal, filters);
}
